import React, { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FlyControls } from "three/examples/jsm/controls/FlyControls";
import { gsap } from "gsap";
import mapboxgl from "mapbox-gl";
import Settings from "./Components/Settings/Settings";
import {
  ARROWS_DISTANCE,
  ARROWS_ROTATION,
  PODIUM_SCALE,
  ROTATE_FLOORS,
  ROTATE_MODEL,
  TRANSPARENCY,
  X,
} from "./constants";

import { Reflector } from "three/examples/jsm/objects/Reflector.js";
import gui from "./utils/gui";
import { TilesRenderer } from '3d-tiles-renderer';
import SkyController from "./Components/Enviroment/SkyController";

import Models from "./Components/Models/Models";
import {
  animateCamera,
  animateCameraPosition,
  calculateDestinationPoint,
  createArrowAndLabel,
  createDropLabel,
  createGradientCircle,
  createLabel,
  createLine,
  createPodium,
  createSunPosition,
  createUserMesh,
  generateRadialFloorTexture,
  getCurrentViewData,
  handleCircleSize,
} from "./utils/threeJsUtils";
import { cameraRenderDistance } from "./Components/Enviroment";
import CameraController, {
  initialCameraHeight,
  maxPolarAngle,
  minPolarAngle,
} from "./Components/CameraController";
import { loadModelGLTF } from "./utils/loadingManager";
import Grid from "./Components/Grid";
import useKey from "./hooks/useKey";

import Presets from "./Components/Presets";
import ApartmentSettings from "./Components/Settings/ApartmentSettings";
import EnvironmentManager from "./Components/EnvironmentManager";
import effectController from "./Components/Enviroment/effectsController";
import TouredApartment from "./Components/TouredApartment";
import useMovement from "./hooks/useMovement";
import VideoScreen from "./Components/VideoScreen";
import SpotLight from "./Components/SpotLight";
import MyPointLight from "./Components/MyPointLight.";
import TexturesManager from "./Components/TexturesManager/TexturesManager";

import ModelsManager from "./Components/Models/ModelsManager";
import LightsManager from "./Components/LightsManager/LightsManager";
import { initialLineWidth } from "./Components/Settings/defaultSettings";
import MovementControls from "./Components/MovementControls";
import MobileMovementController from "./Components/MobileMovementController";
import Points from "./Components/Settings/Points";
import TileSet from "./Components/TileSet";

// import { OGC3DTile } from '@jdultra/threedtiles';
export const DEFAULT = "Default";
export const APARTMENT = "Apartment";
export const FLOOR = "Floor";
export const BUILDING = "Building";
export const ORBIT = "Orbit";
export const STEADY = "Steady";
export const modes = {
  [DEFAULT]: {
    cameraMode: ORBIT,
  },
  [APARTMENT]: {
    cameraMode: STEADY,
  },
};
mapboxgl.accessToken =
  "pk.eyJ1IjoieWFtaXRoYWZha290IiwiYSI6ImNsaWhsMnBpczBxaTkzZHFyZDdpaWp4c3MifQ.N2Batt6nrfHv-rjUo_EYAQ";

const Scene = ({
  setMousePosition,
  viewedApartment,
  touredApartment,
  saveApartmentViewData,
  saveBuildingCalibration,
  saveProjectSettings,
  hoveredApartment,
  buildings,
  floors,
  floor,
  tileSetUrl,
  // modelUrl,
  models,
  show3dMap,
  isRtl,
  setModelReady,
  selectedBuildingName,
  apartments,
  onApartmentClick,
  onApartmentHover,
  colors,
  projectDefaultView,
  saveStatus,
  setSelectedBuildingName,
  skyImage,
  isAdmin,
  onAnimationEnd,
  cameraPosition,
  onBackButtonClick,
}) => {
  const [initialized, setInitialized] = useState(false);
  const [animationEnded, setAnimationEnded] = useState(
    projectDefaultView.showInitialAnimation ? false : true
  );
  const [modelsLoaded, setModelsLoaded] = useState(true);
  const [environmentPreset, setEnvironmentPreset] = useState("Initial");
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [showGrid, setShowGrid] = useState(false);
  const [selectedApartmentNode, setSelectedApartmentNode] = useState();
  const [lastCameraViewData, setLastCameraViewData] = useState();
  const [showPresets, setShowPresets] = useState(true);
  let targetPosition;
  let vacantColor = useRef();
  let occupiedColor = useRef();
  let vacantHoveredColor = useRef();
  let occupiedHoveredColor = useRef();
  let arrowsRefs = useRef([]);
  let arrowsGroup = useRef(new THREE.Group());
  const mountRef = useRef(null);
  const currentMode = useRef(DEFAULT);
  const movementRef = useRef({});
  let raycaster;
  let mouse;
  let pmremGenerator;
  let flyControls;

  movementRef.current = useMovement({ currentMode: currentMode.current });

  const gridSize =
    projectDefaultView && projectDefaultView.maxDistance
      ? projectDefaultView.maxDistance * 3
      : 600; // This defines the size of the grid, adjust as needed
  const appRef = useRef();
  const mainCircle = useRef();
  const southBuildingCircle = useRef();
  const northBuildingCircle = useRef();
  const controls = useRef();
  const renderer = useRef();
  const tileSetRef = useRef();
  const scene = useRef();
  const camera = useRef();
  const modelsRef = useRef({});
  const appTypeModelsRef = useRef({});
  const mousePosition = useRef();
  const apartmentNodes = useRef([]);
  const floorsNodes = useRef([]);
  const movedFloorNodes = useRef([]);
  const hiddenNodes = useRef([]);
  const floorLines = useRef([]);
  const floorLabels = useRef([]);
  const currentSkyDome = useRef("Day");
  const floorLinesWidth = useRef(initialLineWidth);
  const clockRef = useRef();
  const tilesRuntimeRef = useRef();
  const floorLineStartXOffset = useRef(0);
  const podiumRef = useRef();
  const grid = useRef();
  const lookAtPosition = useRef();
  const boundingBox = useRef();
  const spritesNodesRef = useRef([]);
  const areaNodesRef = useRef([]);
  // const shiftPressed = useRef(false);
  const tileSetGroup = useRef(new THREE.Group());
  // const lastCameraViewData = useRef();
  const humanGroupRef = useRef(new THREE.Group());
  const floorMat = useRef(
    new THREE.MeshStandardMaterial({
      roughness: 0.8,
      color: 0xffffff,
      metalness: 0.2,
      bumpScale: 1,
    })
  );
  const initialLookAt = new THREE.Vector3(0, initialCameraHeight, 0);
  let mouseDown = false;

  let initialArrowsRadius = 100; // Set the radius as per your requirement
  // const source =
  //   "http://east.local/wp-content/uploads/2023/03/FullBuildings_v01.glb";
  const gradientColors = [
    {
      position: 0,
      color: "black",
    },
    {
      position: 0.7,
      color: "black",
    },
    {
      position: 1,
      color: colors.primary,
    },
  ];
  // useEffect(() => {
  //   const observer = new IntersectionObserver(
  //     ([entry]) => {
  //       isVisibleRef.current = entry.isIntersecting;
  //       if (entry.isIntersecting) {
  //         console.log("Three.js component is visible");
  //         // Perform any actions needed when the component becomes visible
  //       } else {
  //         console.log("Three.js component is not visible");
  //         // Perform any actions needed when the component is not visible
  //       }
  //     },
  //     { threshold: 0.1 }
  //   );

  //   if (mountRef.current) {
  //     observer.observe(mountRef.current);
  //   }

  //   return () => {
  //     if (mountRef.current) {
  //       observer.unobserve(mountRef.current);
  //     }
  //   };
  // }, []);
  useEffect(() => {
    targetPosition = new THREE.Vector3();
    vacantColor.current = new THREE.MeshBasicMaterial({
      color: colors.primary,
      transparent: true,
      opacity: projectDefaultView.transparency
        ? projectDefaultView.transparency
        : 0.3,
    });
    occupiedColor.current = new THREE.MeshBasicMaterial({
      color: 0xff8870,
      transparent: true,
      opacity: projectDefaultView.transparency
        ? projectDefaultView.transparency
        : 0.3,
    });
    vacantHoveredColor.current = new THREE.MeshBasicMaterial({
      color: colors.primary,
      transparent: true,
      opacity: 0.9,
    });
    occupiedHoveredColor.current = new THREE.MeshBasicMaterial({
      color: "FF8870",
      transparent: true,
      opacity: 0.9,
    });

    !scene.current && init();
    // console.log("CREATING MAIN CIRCLE")

    const direction = new THREE.Vector3(0, 0, -1); // Default direction
    const length = 2; // Length of the arrow
    const color = 0xffff00; // Color of the arrow
    const arrowHelper = new THREE.ArrowHelper(
      direction,
      humanGroupRef.current.position,
      length,
      color
    );
    humanGroupRef.current.add(arrowHelper);

    southBuildingCircle.current = createGradientCircle(
      29,
      -18,
      0,
      18,
      gradientColors
    );
    scene.current.add(southBuildingCircle.current);
    northBuildingCircle.current = createGradientCircle(
      29,
      18,
      0,
      -20,
      gradientColors
    );
    scene.current.add(northBuildingCircle.current);
    return () => {
      floorLabels.current = [];
      floorLines.current = [];
      movedFloorNodes.current = [];
      floorsNodes.current = [];
      apartmentNodes.current = [];
      arrowsRefs.current = [];
      // mainCircle.current = null;
      // console.log("CLEANINGGGGGGGG");
      // Remove the renderer from the DOM
      // mountRef.current.removeChild(renderer.current.domElement);
    };
  }, []);

  useEffect(() => {
    setInputHandlers();
  }, [lastCameraViewData]);
  useEffect(() => {
    console.log("projectDefaultView", projectDefaultView);
    if (!projectDefaultView.showInitialAnimation) onAnimationEnd();

    if (projectDefaultView) {
      // projectDefaultView.podiumScale && changePodiumSize(projectDefaultView.podiumScale)
      floorLinesWidth.current = projectDefaultView.lineWidth
        ? +projectDefaultView.lineWidth
        : initialLineWidth;
      floorLineStartXOffset.current = projectDefaultView.lineStart
        ? +projectDefaultView.lineStart
        : 0;
      if (!projectDefaultView.showOcean) {
        setShowGrid(true);
      } else {
        setShowGrid(false);
      }
    }

    return () => { };
  }, [projectDefaultView]);
  useEffect(() => {
    if (cameraPosition) {
      console.log("cameraPosition", cameraPosition);
      disableControls();


      animateCamera(
        camera.current,
        cameraPosition.position,
        cameraPosition.quaternion,
        1
      );
      // setControlsTarget(lootAtTarget)
      // animateLookAtPosition(cameraPosition.position, 1);
    }
  }, [cameraPosition]);

  useEffect(() => {
    // console.log({ hoveredApartment });
    if (hoveredApartment && currentMode.current === DEFAULT)
      setAllApartmentsNodes([hoveredApartment.id]);
  }, [hoveredApartment]);

  useEffect(() => {
    console.log("viewedApartment", viewedApartment);
    if (viewedApartment) {
      setAllApartmentsNodes();
      enableControls();
      if (
        viewedApartment.default_view &&
        viewedApartment.default_view.position
      ) {
        targetPosition = viewedApartment.default_view.position;
        animateCameraPosition(camera.current, targetPosition, 1);
      }
    }
  }, [viewedApartment]);
  useEffect(() => {
    handleTouredApartment();
  }, [touredApartment]);
  const handleTouredApartment = async () => {
    console.log("touredApartment", touredApartment);

    if (touredApartment) {
      onApartmentHover();
      setAllApartmentsNodes();
      setShowGrid(false)
      // console.log("apartmentNodes.current", apartmentNodes.current);
      const buildingRotation = touredApartment.type_building.default_view.rotation
      const apartmentNode = apartmentNodes.current.find(
        (node) => node.userData.apartment.id == touredApartment.id
      );
      // console.log("apartmentNode", apartmentNode);
      // console.log(appTypeModelsRef.current)
      if (!apartmentNode) return;
      // console.log("apartmentNode", apartmentNode);
      // apartmentNode.visible = false;
      // return
      const appType = apartmentNode.userData.apartment.apartment_type;
      // console.log("appType", appType);
      if (appType) {
        // console.log("apartmentType", appType);
        if (appTypeModelsRef.current[appType.name]) {
          if (currentMode.current === DEFAULT) setLastCameraViewData(getCurrentViewData(camera.current));
          disableControls();
          currentMode.current = APARTMENT;

          const worldPosition = new THREE.Vector3();
          apartmentNode.getWorldPosition(worldPosition);
          // console.log("apartmentNode worldPosition", worldPosition);
          const typeNode = appTypeModelsRef.current[appType.name];
          // console.log("typeNode", typeNode);
          typeNode.visible = true;
          typeNode.position.set(worldPosition.x, worldPosition.y, worldPosition.z);
          typeNode.rotation.set(0, buildingRotation._y, 0);
          typeNode.children.forEach((child) => {
            child.visible = true;
          });

          setSelectedApartmentNode(typeNode);
          setShowPresets(false);
          setTimeout(async () => {
            await moveToApartmentNode(typeNode);
            const floor = floors.find((f) => f.id === touredApartment.floor_id);
            // toggleEntitiesVisibility("modelsRef", false);
            setSelectedFloor(floor);
          }, 300);

        }
      }

    } else {
      appRef.current = null;
      setShowPresets(true);
      setSelectedFloor(null);
      if (!projectDefaultView.showOcean) {
        setShowGrid(true);
      } else {
        setShowGrid(false);
      }
      toggleEntitiesVisibility("modelsRef", true);
      // Object.keys(modelsRef.current).forEach((key) => {
      //   modelsRef.current[key].visible = true;
      // })
      setSelectedApartmentNode(null);
      apartmentNodes.current.forEach((node) => {
        node.visible = true;
      });
      Object.keys(appTypeModelsRef.current).forEach((key) => {
        appTypeModelsRef.current[key].position.y = 1000
        appTypeModelsRef.current[key].visible = false;
      });
      currentMode.current = DEFAULT;
      // setSelectedFloor(null);
      // resetCameraToLastView();

    }
  }


  useEffect(() => {
    handleSelectedBuildingName();
    return () => { };
  }, [selectedBuildingName, modelsRef.current, mainCircle.current]);
  useEffect(() => {
    handleFloorAnimation();

    return () => { };
  }, [floor]);
  useEffect(() => {
    handlePresetChange();
  }, [environmentPreset]);
  // useEffect(() => {
  //   if (selectedFloor) {
  //     // toggleModelsVisibility(false)
  //   } else {
  //     // toggleModelsVisibility(true)
  //   }
  // }, [selectedFloor]);
  useEffect(() => {
    console.log("models", models);
    if (models) {
      buildModels();
    } else {
      // toggleModelsVisibility(true)
    }
  }, [models]);

  const moveCameraToLastView = useCallback(() => {
    console.log("resetCameraToLastView", lastCameraViewData);
    if (lastCameraViewData) {
      setTimeout(() => {          // lookAtPosition.current = initialLookAt;
        animateLookAtPosition(initialLookAt, 1);
        animateCamera(
          camera.current,
          lastCameraViewData.position,
          lastCameraViewData.quaternion,
          1
        );

      }, 200);
    }
  }, [lastCameraViewData]);
  const applyMaterialsToApartment = (appartmentMesh) => {
    if (appartmentMesh) {
      const liveRoom = appartmentMesh.children.find((child) =>
        child.name.includes("liveroom")
      );
      if (liveRoom) {
        liveRoom.material = floorMat.current;
      }
    }
  };
  function buildModels() {

    const modelsGroup = new THREE.Group();
    modelsGroup.name = "modelsGroup";
    modelsGroup.visible = false;

    buildings.forEach((building, index) => {
      // console.log("building", building);
      // create a group to fold floor lines and labels
      const floorLinesGroup = new THREE.Group();

      if (models[building.id]) {

        const model = models[building.id];
        model.castShadow = true;
        model.receiveShadow = true;
        if (building.default_view) {
          if (building.default_view.position) {
            model.position.set(
              building.default_view.position.x,
              0,
              building.default_view.position.z
            );
            floorLinesGroup.position.set(
              building.default_view.position.x,
              0,
              building.default_view.position.z
            );
          }

          if (building.default_view.rotation) {
            model.rotation.set(0, building.default_view.rotation._y, 0);
          }

          floorLinesGroup.rotation.set(
            0,
            building.default_view.floorsLinesRotationY
              ? building.default_view.floorsLinesRotationY
              : 0,
            0
          );
          if (building.default_view.scale) {
            model.scale.set(
              building.default_view.scale.x,
              building.default_view.scale.x,
              building.default_view.scale.x
            );
            floorLinesGroup.scale.set(
              building.default_view.scale.x,
              building.default_view.scale.x,
              building.default_view.scale.x
            );
          }
        } else {
          model.position.set(0, 0, 0);
          floorLinesGroup.position.set(0, 0, 0);
        }
        model.traverse(function (node) {
          if (node.isMesh) {
            // node.castShadow = true;ו
          }
          if (node.name.includes("Floor") && node.name.includes("base")) {

            node.userData["buildingName"] = building.name;
            floorsNodes.current.push(node);

            const floorLabel = createLabel(node.name.split("_")[1]);
            let endPos,
              startPos = {
                x: model.position.x,
                y: node.position.y,
                z: node.position.z,
              };
            let lineWidth = projectDefaultView.lineWidth
              ? +projectDefaultView.lineWidth
              : initialLineWidth;

            if (model.position.x > 0) {
              endPos = {
                x: model.position.x + lineWidth,
                y: node.position.y,
                z: model.position.z,
              };
            } else {
              endPos = {
                x: model.position.x - lineWidth,
                y: node.position.y,
                z: model.position.z,
              };
            }
            const line = createLine(
              startPos,
              endPos,
              projectDefaultView.floorLineColor
                ? projectDefaultView.floorLineColor
                : "white"
            );
            scene.current.add(line);
            line.userData.startPos = startPos;
            line.userData.endPos = endPos;
            floorLabel.position.set(endPos.x, endPos.y, endPos.z); // Position it above the arrow
            line.label = floorLabel;
            floorLines.current.push(line);
            floorLabels.current.push(floorLabel);
            floorLabel.scale.set(10, 10, 1); // Position it above the arrow
            floorLinesGroup.add(line);
            floorLinesGroup.add(floorLabel);
            floorLinesGroup.visible = building.show_floors_lines;
          }

          if (node.name.includes("bbox")) {
            // console.log("bbox node", node.children);
            const balconyNode = node.children.find((child) => child.name.includes("balcony"));
            if (balconyNode) {
              // console.log("balconyNode", balconyNode);
              const appType = node.userData.apartment.apartment_type;
              // console.log("is type node", appType.name, node);

              if (!appTypeModelsRef.current[appType.name]) {
                // console.log("appType.name", appType.name);
                const clonedNode = node.clone();
                clonedNode.material.opacity = 0
                if (building.default_view.scale) {
                  clonedNode.scale.set(
                    building.default_view.scale.x,
                    building.default_view.scale.y,
                    building.default_view.scale.z
                  );
                }
                scene.current.add(clonedNode);
                appTypeModelsRef.current[appType.name] = clonedNode;
              }
            }

            if (node.userData.apartment) {

              apartmentNodes.current.push(node);
              if (
                (node.userData.apartment.available === "פנוי") ||
                node.userData.apartment.available === "Available"
              ) {
                node.material = vacantColor.current;
              } else {
                node.material = occupiedColor.current;
              }
            }
          }
          if (node.name.includes("area")) {
            node.visible = false
            areaNodesRef.current.push(node);
            node.material = occupiedHoveredColor.current;
            const label = createDropLabel(node.name.split("_").join(" "), "black", "white");
            scene.current.add(label);
            label.position.set(node.position.x, node.position.y + 20, node.position.z);
          }
          if (node.name.includes("label")) {
            node.visible = false
            const nodePosition = node.position;
            const buildingLabel = createDropLabel(node.name.split("_")[1], "black", "white");
            scene.current.add(buildingLabel);
            buildingLabel.position.set(nodePosition.x, nodePosition.y + 30, nodePosition.z);
            spritesNodesRef.current.push(buildingLabel);
            scene.current.add(buildingLabel);
          }
        });
        scene.current.add(model);
        // modelsGroup.add(model);
        const circleRadius = 30;

        const circle = createGradientCircle(
          circleRadius,
          building.default_view && building.default_view.position
            ? building.default_view.position.x
            : 0,
          building.default_view && building.default_view.position
            ? building.default_view.position.y
            : 0,
          building.default_view && building.default_view.position
            ? building.default_view.position.z
            : 0,
          gradientColors,
          false
        );
        scene.current.add(circle);
        if (building.default_view && building.default_view.circleRadiusOffset) {
          handleCircleSize(building.default_view.circleRadiusOffset, circle);
        }
        modelsRef.current[building.name] = model;
        modelsRef.current[building.name].circle = circle;
        modelsRef.current[building.name].floorsGroup = floorLinesGroup;
        modelsGroup.add(floorLinesGroup);

      } else {

        console.warn(
          "models not found for building: " + building.name + " id: " + building.id
        );
      }
    });

    setModelReady(true);
  }
  const handlePresetChange = () => {

    if (environmentPreset === "Grid") {
      setShowGrid(true);
    }
    else {

      if (!projectDefaultView.showOcean) {
        setShowGrid(true);
        podiumRef.current && (podiumRef.current.visible = true);
      }
      else {
        setShowGrid(false);
        podiumRef.current && (podiumRef.current.visible = false);
      }
    }

    toggleEntitiesVisibility("arrows", environmentPreset !== "Floors");

  };


  const toggleEntitiesVisibility = (entitiyName, visible) => {
    switch (entitiyName) {
      case "modelsRef":
        Object.keys(modelsRef.current).forEach((key) => {
          modelsRef.current[key].visible = visible;
        });

        break;
      case "arrows":
        if (arrowsGroup.current)
          arrowsGroup.current.visible = visible;
      // arrowsRefs.current.forEach((arrow) => {
      //   arrow.visible = visible;
      // });
      default:
        break;
    }
  };

  function init() {
    clockRef.current = new THREE.Clock();
    mouse = new THREE.Vector2();
    scene.current = new THREE.Scene();
    raycaster = new THREE.Raycaster();
    createRenderer();
    pmremGenerator = new THREE.PMREMGenerator(renderer.current);
    pmremGenerator.compileEquirectangularShader();
    setCamera();

    loadDirectionArrows();

    scene.current.add(podiumRef.current);


    humanGroupRef.current.add(createUserMesh());
    window.addEventListener("resize", function () {
      var width = window.innerWidth;
      var height = window.innerHeight;
      renderer.current.setSize(width, height);
      camera.current.aspect = width / height;
      camera.current.updateProjectionMatrix();
    });

    const applyMoveAndRotate = (direction) => {
      // console.log(movementRef.current.moveLeft)
      const {
        moveForward,
        moveBackward,
        moveLeft,
        moveRight,
        moveUp,
        moveDown,
        lookLeft,
        lookRight,
        lookUp,
        lookDown,
      } = movementRef.current;
      if (moveUp) {
        moveCamera("up");
      }
      if (moveDown) {
        moveCamera("down");
      }
      if (moveLeft) {
        moveCamera("left");
      }
      if (moveRight) {
        moveCamera("right");
      }
      if (moveForward) {
        moveCamera("forward");
      }
      if (moveBackward) {
        moveCamera("backward");
      }
      if (lookLeft) {
        rotateCamera("left");
      }
      if (lookRight) {
        rotateCamera("right");
      }
      if (lookUp) {
        rotateCamera("up");
      }
      if (lookDown) {
        rotateCamera("down");
      }
    };
    function animate() {

      requestAnimationFrame(animate);

      applyMoveAndRotate();
      // lookAtPosition.current && setControlsTarget(lookAtPosition.current);
      humanGroupRef.current.rotation.copy(camera.current.rotation);
      humanGroupRef.current.position.set(
        camera.current.position.x,
        0,
        camera.current.position.z
      );
      // controls.current && console.log(controls.current.target)
      controls.current && controls.current.update(); // Only required if controls.enableDamping = true, or if controls.autoRotate = true
      camera.current.updateMatrixWorld();
      // console.log(camera.position)
      // if (tilesRenderer.current && camera.current && renderer.current)
      // tilesRenderer.current.update();
      // camera.current.lookAt(tilesRenderer.current.group.position);
      renderer.current.render(scene.current, camera.current);
    }

    if (renderer.current && camera.current) {
      animate();
    }
    setInitialized(true);
  }
  async function handleFloorAnimation() {
    let targetPosition;
    console.log("handleFloorAnimation", floor);
    if (floor) {
      console.log(floorsNodes.current)
      const floorsByBuilding = floorsNodes.current.filter(
        (node) => node.userData.buildingName == floor.building
      );
      console.log("floorsByBuilding", floorsByBuilding);
      const floorNode = floorsByBuilding.find(
        (node) => +getFloorNumFromName(node.name) == floor.name
      );
      console.log({ floorNode });
      if (floorNode) {

        const floorsAbove = floorsByBuilding.filter(
          (node) =>
            +getFloorNumFromName(node.name) > +floor.name
        );
        console.log("floorsAbove", floorsAbove);
        const sortedByFloorNum = floorsAbove.sort(
          (a, b) => +getFloorNumFromName(a.name) - +getFloorNumFromName(b.name)
        );
        // console.log(floorsAbove)
        movedFloorNodes.current = sortedByFloorNum;
        // console.log("movedFloorNodes.current", movedFloorNodes.current);
        gsap.utils
          .toArray(movedFloorNodes.current.reverse())
          .forEach((child, i) => {
            gsap.to(child.position, {
              y: child.position.y + 1000,
              duration: 1,
              ease: "power2.out",
              delay: i * 0.05, // Add delay here for stagger effect
            });
          });
        const model = modelsRef.current[floor.building];
        // console.log(model.position);
        const building = buildings.find((b) => b.name == floor.building);
        // console.log(building.default_view);
        const movedFloorsNames = movedFloorNodes.current.map(
          (node) => node.name
        );
        model.traverse(function (node) {
          if (
            node.isMesh &&
            node.position.y > floorNode.position.y &&
            !movedFloorsNames.includes(node.name)
          ) {
            node.visible = false;
            hiddenNodes.current.push(node);
          }
        });
        // console.log({ building });
        if (building.default_view && building.default_view.floorsView) {
          targetPosition = new THREE.Vector3(
            building.default_view.floorsView.position.x,
            (floorNode.position.y + 50) * building.default_view.scale.y,
            building.default_view.floorsView.position.z
          );
          const lookAt = new THREE.Vector3(
            targetPosition.x,
            targetPosition.y - 50,
            targetPosition.z
          );
          floorLines.current.forEach((line) => (line.visible = false));
          floorLabels.current.forEach((label) => (label.visible = false));
          animateLookAtPosition(lookAt, 4);
          await animateCameraPosition(camera.current, targetPosition, 3);
        }
      }
    } else {
      if (movedFloorNodes.current && movedFloorNodes.current.length) {
        console.log("movedFloorNodes.current", movedFloorNodes.current);
        gsap.utils
          .toArray(movedFloorNodes.current.reverse())
          .forEach((child, i) => {
            gsap.to(child.position, {
              y: child.position.y - 1000,
              duration: 1,
              ease: "power2.out",
              delay: i * 0.05, // Add delay here for stagger effect
              onComplete: () => {
                movedFloorNodes.current = [];
              },
            });
          });

        floorLines.current.forEach((line) => (line.visible = true));
        floorLabels.current.forEach((label) => (label.visible = true));
        hiddenNodes.current.forEach((node) => (node.visible = true));
      }
      moveToDefaultView();
    }
  }
  function setControlsTarget(position) {
    controls.current &&
      controls.current.target.set(position.x, position.y, position.z);
  }
  function animateLookAtPosition(
    targetPosition,
    duration,
    easeName = "power4.out"
  ) {
    if (targetPosition) {
      return new Promise((resolve) => {
        gsap.to(controls.current.target, {
          duration: duration,
          x: targetPosition.x,
          y: targetPosition.y,
          z: targetPosition.z,
          ease: easeName, // or any other easing function
          // onUpdate: () => {
          //   camera.updateProjectionMatrix();
          // }, // required when the camera position is changing
          onComplete: resolve,
        });
      });
    }
  }
  const handleSelectedBuildingName = () => {
    console.log("handleSelectedBuildingName", selectedBuildingName);

    // if (mainCircle.current) {
    const selectedBuilding = buildings.find(
      (b) => b.name == selectedBuildingName
    );
    if (selectedBuilding) {
      console.log("selectedBuilding", selectedBuilding);
      // mainCircle.current.visible = false;
      if (
        selectedBuilding &&
        selectedBuilding.default_view &&
        selectedBuilding.default_view.camera
      ) {
        targetPosition = selectedBuilding.default_view.camera.position;
        const lookAt = calculateDestinationPoint(
          {
            position: selectedBuilding.default_view.camera.position,
            quaternion: selectedBuilding.default_view.camera.quaternion,
          },
          100
        );
        // lookAtPosition.current = lookAt;
        animateCamera(
          camera.current,
          selectedBuilding.default_view.camera.position,
          selectedBuilding.default_view.camera.quaternion,
          1
        );


      }

      handleSelectedBuildingForEastProjectOnly(selectedBuildingName);
    } else {
      // mainCircle.current.visible = true;
      // Object.keys(modelsRef.current).forEach((key) => {
      //   modelsRef.current[key].circle.visible = false;
      // });
    }
    // }
    //  console.log(mainCircle.current)
  };
  const handleSelectedBuildingForEastProjectOnly = (selectedBuildingName) => {
    const southCamera = {
      position: {
        x: 89.97815368124795,
        y: 146.98262272594656,
        z: 137.82802698255836,
      },
      rotation: {
        x: -0.32852584006224983,
        y: 0.5534883968551999,
        z: 0.17730393668770358,
      },
      quaternion: {
        w: -0.1328207537746399,
        x: -0.1328207537746399,
        y: 0.2824157959584761,
        z: 0.039516937487675756,
      },
    };
    const northCamera = {
      position: {
        x: 142.8246311892743,
        y: 143.66212649833335,
        z: -83.63458277107074,
      },
      rotation: {
        x: -2.660454543596068,
        y: 0.9870258628409659,
        z: 2.730777725044315,
      },
      quaternion: {
        w: -0.06396059933432448,
        x: -0.06396059933432448,
        y: 0.8603535706333537,
        z: 0.11157352920821557,
      },
    };
    let targetPosition;
    if (
      selectedBuildingName == "מגדל דרומי" ||
      selectedBuildingName == "Southern tower"
    ) {
      targetPosition = southCamera.position;
      animateCameraPosition(camera.current, targetPosition, 1);
      if (southBuildingCircle.current)
        southBuildingCircle.current.visible = true;
      if (northBuildingCircle.current)
        northBuildingCircle.current.visible = false;
    } else if (
      selectedBuildingName == "מגדל צפוני" ||
      selectedBuildingName == "Northern tower"
    ) {
      targetPosition = northCamera.position;
      animateCameraPosition(camera.current, targetPosition, 1);
      // if (southBuildingCircle.current)
      //   southBuildingCircle.current.visible = false;
      // if (northBuildingCircle.current)
      //   northBuildingCircle.current.visible = true;
    }
  };


  const loadDirectionArrows = async () => {
    // Create gradient
    let canvas = document.createElement("canvas");
    canvas.width = 2;
    canvas.height = 2;

    let context = canvas.getContext("2d");
    context.fillStyle = colors.primary;
    context.fillRect(0, 0, 1, 2);

    // context.fillStyle = "black";
    context.fillRect(1, 0, 1, 2);

    // Create texture
    let texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;

    const height = 5;
    const arrow = await loadModelGLTF(require("./arrow_3.glb"));
    arrow.traverse(function (node) {
      if (node.isMesh) {
        node.material.map = texture;
        node.material.needsUpdate = true;
      }
    });
    arrow.scale.set(50, 50, 10); // Scale the model
    arrow.rotation.x = Math.PI / 2; // Rotate 90 degrees around the x-axis
    arrow.position.y = height;
    let radius = initialArrowsRadius;
    if (projectDefaultView) {
      if (projectDefaultView.arrowsRadiusOffset)
        radius += +projectDefaultView.arrowsRadiusOffset;
      if (projectDefaultView.arrowsRotation)
        arrowsGroup.current.rotation.y = +projectDefaultView.arrowsRotation / 10;
    }
    const arrowsData = [
      {
        labelName: "S",
        position: [-radius, arrow.position.y, arrow.position.z],
        rotation: [arrow.rotation.x, arrow.rotation.y, Math.PI],
        labelPosition: [-radius, height + 4, 0],
      },
      {
        labelName: "W",
        position: [arrow.position.x, arrow.position.y, -radius],
        rotation: [arrow.rotation.x, arrow.rotation.y, -Math.PI / 2],
        labelPosition: [0, height + 4, -radius],
      },
      {
        labelName: "E",
        position: [arrow.position.x, arrow.position.y, radius],
        rotation: [arrow.rotation.x, arrow.rotation.y, Math.PI / 2],
        labelPosition: [0, height + 4, radius],
      },
      {
        labelName: "N",
        position: [radius, arrow.position.y, arrow.position.z],
        rotation: [arrow.rotation.x, arrow.rotation.y, arrow.rotation.z],
        labelPosition: [radius, height + 4, 0],
      },
    ];
    arrowsData.forEach((arrowData) => {
      const arrowGroup = createArrowAndLabel(
        arrow,
        arrowData.position,
        arrowData.rotation,
        arrowData.labelName,
        arrowData.labelPosition
      );
      // arrowsRefs.current.push(arrowGroup);
      arrowsGroup.current.add(arrowGroup);

    });

    scene.current.add(arrowsGroup.current);
  };


  const changePodiumSize = (size) => {
    // console.log(podiumRef.current);
    podiumRef.current.scale.set(size, 1, size);
  };
  const createRenderer = () => {
    renderer.current = new THREE.WebGLRenderer({ antialias: true });
    renderer.current.setSize(window.innerWidth, window.innerHeight);
    renderer.current.shadowMap.enabled = true; // Enable shadow maps
    // renderer.current.shadowMap.type = THREE.PCFSoftShadowMap;
    const mapDiv = document.getElementById("map");
    renderer.current.domElement.addEventListener(
      "webglcontextrestored",
      function () { },
      false
    );
    mapDiv.prepend(renderer.current.domElement);
  };

  const setFlyControls = () => {
    // Assuming you have initialized camera and renderer
    flyControls = new FlyControls(camera, renderer.current.domElement);

    flyControls.movementSpeed = 1.0;
    flyControls.domElement = renderer.current.domElement;
    flyControls.rollSpeed = Math.PI / 12;
    flyControls.autoForward = false;
    flyControls.dragToLook = false;
  };
  const addLighting = () => {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.current.add(ambientLight);
  };
  const addSunlight = () => {
    const sunlight = new THREE.DirectionalLight(0xffffff, 1);
    sunlight.position.set(200, 200, 100); // Position the light
    sunlight.castShadow = true; // Enable shadows for the light

    // Configure shadow properties
    sunlight.shadow.mapSize.width = 2048; // Shadow map resolution
    sunlight.shadow.mapSize.height = 2048;
    sunlight.shadow.camera.near = 0.5; // Near clipping plane
    sunlight.shadow.camera.far = 1000; // Far clipping plane
    sunlight.shadow.camera.left = -500; // Left extent of the shadow camera
    sunlight.shadow.camera.right = 500; // Right extent of the shadow camera
    sunlight.shadow.camera.top = 500; // Top extent of the shadow camera
    sunlight.shadow.camera.bottom = -500; // Bottom extent of the shadow camera

    scene.current.add(sunlight);
  };
  const getLinePositions = (line) => {
    // Get the positions attribute from the geometry
    const positions = line.geometry.attributes.position.array;

    // The start position is the first vertex
    const start = new THREE.Vector3(positions[0], positions[1], positions[2]);

    // The end position is the second vertex
    const end = new THREE.Vector3(positions[3], positions[4], positions[5]);

    return { start, end };
  };

  const setCamera = () => {
    camera.current = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      cameraRenderDistance * 2
    );
  };
  const calculatePolarAngle = (camera) => {
    const position = camera.position;
    const spherical = new THREE.Spherical();
    spherical.setFromVector3(position);
    return spherical.phi; // phi is the polar angle
  };
  const setControls = () => {
    // console.log("setControls");
    if (controls.current) return;
    controls.current = new OrbitControls(
      camera.current,
      renderer.current.domElement
    );
    controls.current.screenSpacePanning = false;
    // lookAtPosition.current = new THREE.Vector3(0, initialCameraHeight, 0);
    controls.current.target.set(0, initialCameraHeight, 0);

    controls.current.enabled = false;
    controls.current.enableRotate = false;
    controls.current.enableDamping = false;
    controls.current.enableZoom = false;
    controls.current.enablePan = false;
    controls.current.dampingFactor = 0.035;
    controls.current.minPolarAngle = minPolarAngle;
    controls.current.maxPolarAngle = maxPolarAngle;
    // controls.current.minDistance = 50;
    controls.current.maxDistance = projectDefaultView.maxDistance
      ? projectDefaultView.maxDistance
      : 200;

   
    controls.current.update();
  };
  const enableControls = () => {
    // console.log("enableControls", controls.current);
    if (controls.current) {
      controls.current.enabled = true;
      controls.current.enableRotate = true;
      controls.current.enableDamping = true;
      controls.current.enableZoom = true;
      controls.current.enablePan = true;
          controls.current.minPolarAngle = minPolarAngle;
    controls.current.maxPolarAngle = maxPolarAngle;
      controls.current.update();
      // controls.current.dispose(); // Remove all event listeners
      // controls.current = null; // Optionally, set controls to null to indicate they are disabled
    }
  };
  const disableControls = () => {
    // console.log("disableControls", controls.current);
    if (controls.current) {
      controls.current.enabled = false;
      controls.current.enableRotate = false;
      controls.current.enableDamping = false;
      controls.current.enableZoom = false;
      controls.current.enablePan = false;
      // controls.current.dispose(); // Remove all event listeners
      // controls.current = null; // Optionally, set controls to null to indicate they are disabled
    }
  };
  const getDirection = (currentDirection, targetDirection) => {
    // Create a quaternion for a 90-degree rotation around the Y-axis
    let quaternion = new THREE.Quaternion();

    // console.log({currentDirection})
    // Apply the quaternion to the current direction vector
    if (targetDirection == "left") {
      quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      return currentDirection.clone().applyQuaternion(quaternion);
    } else {
      quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI / 2);
      return currentDirection.clone().applyQuaternion(quaternion);
    }
  };
  const rotateCamera = (direction) => {
    const speed = 1;
    const currentDirection = camera.current.getWorldDirection(
      new THREE.Vector3()
    );
    lookAtPosition.current = calculateDestinationPoint(
      {
        position: camera.current.position,
        quaternion: camera.current.quaternion,
      },
      100
    );

    switch (direction) {
      case "left":
        const leftDirection = getDirection(currentDirection, "left");
        lookAtPosition.current.x += leftDirection.x;
        lookAtPosition.current.z += leftDirection.z;
        break;
      case "right":
        const rightDirection = getDirection(currentDirection, "right");
        lookAtPosition.current.x += rightDirection.x;
        lookAtPosition.current.z += rightDirection.z;
        break;
      case "up":
        lookAtPosition.current.y += speed;
        break;
      case "down":
        lookAtPosition.current.y -= speed;
        break;
      default:
        break;
    }
    setControlsTarget(lookAtPosition.current);
    // console.log("lookAtPosition.current", lookAtPosition.current);
  };
  const getNextPosition = (
    direction,
    currentPosition,
    currentDirection,
    speed
  ) => {
    const newPosition = currentPosition.clone();

    switch (direction) {
      case "up":
        newPosition.y += speed;
        break;
      case "down":
        newPosition.y -= speed;
        break;
      case "forward":
        newPosition.x += currentDirection.x * speed;
        newPosition.z += currentDirection.z * speed;
        break;
      case "backward":
        newPosition.x -= currentDirection.x * speed;
        newPosition.z -= currentDirection.z * speed;
        break;
      case "left":
        const leftDirection = getDirection(currentDirection, "left");
        newPosition.x += leftDirection.x * speed;
        newPosition.z += leftDirection.z * speed;
        break;
      case "right":
        const rightDirection = getDirection(currentDirection, "right");
        newPosition.x += rightDirection.x * speed;
        newPosition.z += rightDirection.z * speed;
        break;
      default:
        break;
    }

    return newPosition;
  };
  function getNearestObjectBelowCamera() {
    if (!camera.current || !scene.current) return null;

    // Raycast straight down from the camera's position
    const origin = camera.current.position.clone();
    const direction = new THREE.Vector3(0, -1, 0); // Downward in world space

    const raycaster = new THREE.Raycaster(origin, direction);
    // Only check meshes in the scene
    const objects = [];
    scene.current.traverse((child) => {
      if (child.isMesh) objects.push(child);
    });

    const intersects = raycaster.intersectObjects(objects, true);
    if (intersects.length > 0) {
      return intersects[0]; // Closest object below the camera
    }
    return null;
  }
  const moveCamera = (direction) => {
    // console.log("moveCamera", direction);
    let speed = 1;
    if (currentMode.current === APARTMENT) speed = 0.03;
    const currentDirection = camera.current.getWorldDirection(
      new THREE.Vector3()
    );
    const currentPosition = camera.current.position.clone();
    let nextPosition = getNextPosition(
      direction,
      currentPosition,
      currentDirection,
      speed
    );

    const raycaster = new THREE.Raycaster();
    const moveVector = nextPosition.clone().sub(currentPosition).normalize();
    // if (boundaryNode.current) {
    //   const boundingBox = new THREE.Box3().setFromObject(boundaryNode.current);
    //   // Check X and Z bounds only
    //   console.log("Bounding Box:", boundingBox);
    //   console.log("nextPosition:", nextPosition);
    //   if (
    //     nextPosition.x < boundingBox.min.x ||
    //     nextPosition.x > boundingBox.max.x ||
    //     nextPosition.z < boundingBox.min.z ||
    //     nextPosition.z > boundingBox.max.z
    //   ) {
    //     console.log("Out of bounds, stopping camera movement");
    //     return;
    //   }
    // }
    // Set the raycaster to detect collisions
    raycaster.set(currentPosition, moveVector);
    raycaster.camera = camera.current; // Set the camera for raycasting against sprites
    const intersects = raycaster.intersectObjects(scene.current.children, true);

    // camera.current.position.copy(nextPosition);
    if (intersects.length > 0 && intersects[0].distance < 0.3) {
      // Collision detected, do not move the camera
      console.log("Collision detected, stopping camera movement");
      return
    } else {
      // No collision, move the camera
      // 
      camera.current.position.copy(nextPosition);
      // nextPosition = getNextPosition(
      //   direction,
      //   humanGroupRef.current.position,
      //   currentDirection,
      //   speed
      // );
      // humanGroupRef.current.position.copy(nextPosition);
    }
  };


  const getFloorNumFromName = (name) => {
    return name.split("_")[1];
  };
  const getHighestPointFromIntersections = (intersects) => {
    if (intersects && intersects.length > 0) {
      const highestPoint = intersects.reduce((highest, current) => {
        return current.point.y > highest.point.y ? current : highest;
      });
      return highestPoint.point;
    }
  };
  const getAllMeshesFromIntersections = (intersects) => {
    if (intersects && intersects.length > 0) {
      const meshes = intersects.filter((intersect) => intersect.distance < 500 && intersect.object && intersect.object instanceof THREE.Mesh);
      return meshes;
    }
  };
  async function onPointClick(point) {
    console.log("onPointClick", point);
    if (point) {
      // Move the camera to the clicked point
      currentMode.current = APARTMENT;

      let strightForwardLoogAtPosition = calculateDestinationPoint(
        {
          position: point,
          quaternion: camera.current.quaternion,
        },
        100
      );
      if (!lastCameraViewData) setLastCameraViewData(getCurrentViewData(camera.current));
      const straightForwardQuaterion = new THREE.Quaternion();
      strightForwardLoogAtPosition.y = point.y
      animateLookAtPosition(strightForwardLoogAtPosition, 2);
      // setControlsTarget(heightPoint);
      await animateCamera(camera.current, point, straightForwardQuaterion, 2);
    }
  }
  const getNodesByName = (name) => {
    if (!scene.current) return [];
    const nodes = [];
    scene.current.traverse((child) => {
      if (child.name === name) {
        nodes.push(child);
      }
    });
    return nodes;
  };

  // Separate function to handle sprite clicks
  async function handleSpriteClick(sprite) {
    const labelNode = getNodesByName(sprite.name)[0];
    if (labelNode) {
      console.log("labelNode", labelNode);
      const labelPosition = labelNode.position;
      const lookAtPosition = labelPosition.clone();
      lookAtPosition.y -= 20;

      if (!lastCameraViewData) {
        setLastCameraViewData(getCurrentViewData(camera.current));
      }

      const straightForwardQuaternion = new THREE.Quaternion();
      animateLookAtPosition(lookAtPosition, 1);

      const cameraPosition = {
        x: labelPosition.x,
        y: labelPosition.y,
        z: labelPosition.z + 40
      };

      await animateCamera(camera.current, cameraPosition, straightForwardQuaternion, 1);
    }
  }
  async function onMouseDown(event) {
    mouseDown = true;
    if (!raycaster) return
    if (currentMode.current === DEFAULT) {
      raycaster.setFromCamera(mouse, camera.current);
      const intersects = raycaster.intersectObjects(scene.current.children, true);
      // console.log("intersects", intersects);
      const meshes = getAllMeshesFromIntersections(intersects);
      // console.log("meshes", meshes);


      const spriteIntersects = raycaster.intersectObjects(spritesNodesRef.current, false);

      if (spriteIntersects.length > 0) {
        const clickedSprite = spriteIntersects[0].object;
        console.log("Clicked sprite:", clickedSprite.name);
        console.log(modelsRef.current);
        await handleSpriteClick(clickedSprite);
        return;
      }
      if (!meshes || meshes.length == 0) return;
      const apartmentNode = meshes.find((node) => node.object.name.includes('bbox'));
      const areaNode = meshes.find((node) => node.object.name.includes('area'));

      if (apartmentNode && apartmentNode.object.userData.apartment) {
        onApartmentClick(apartmentNode.object.userData.apartment);
        return
      }
      else if (areaNode) {
        // boundaryNode.current = intersects[0].object;
        let heightPoint = intersects[0].point;
        // let heightPoint = getHighestPointFromIntersections(intersects);
        if (heightPoint) {
          // console.log("heightPoint", heightPoint);
          // Move the camera to the clicked point
          currentMode.current = APARTMENT;
          heightPoint.y = heightPoint.y + 1;
          // console.log("heightPoint", heightPoint);
          let strightForwardLoogAtPosition = calculateDestinationPoint(
            {
              position: heightPoint,
              quaternion: camera.current.quaternion,
            },
            100
          );
          strightForwardLoogAtPosition.y = heightPoint.y;
          setLastCameraViewData(getCurrentViewData(camera.current));
          const straightForwardQuaterion = new THREE.Quaternion();
          animateLookAtPosition(strightForwardLoogAtPosition, 2);
          // setControlsTarget(heightPoint);
          await animateCamera(camera.current, heightPoint, straightForwardQuaterion, 2);
        }
      }
    }
  }
  const setInputHandlers = () => {
    renderer.current.domElement.addEventListener(
      "mousemove",
      onMouseMove,
      false
    );
    renderer.current.domElement.addEventListener(
      "mousedown",
      onMouseDown,
      false
    );
    renderer.current.domElement.addEventListener(
      "mouseup",
      function (event) {
        mouseDown = false;
      },
      false
    );
  };
  const setGrid = () => {
    // Set up the grid

    let divisions = 20; // This defines the number of divisions in the grid, adjust as needed

    grid.current = new THREE.GridHelper(gridSize, divisions);
    // grid.current.scale.set(0, 0, 0);
    scene.current.add(grid.current);

    // // Create a canvas to draw the gradient texture
    // const size = 512;
    // const canvas = document.createElement("canvas");
    // canvas.width = size;
    // canvas.height = size;
    // const context = canvas.getContext("2d");

    // // Create a radial gradient (black to transparent)
    // const gradient = context.createRadialGradient(
    //   size / 2,
    //   size / 2,
    //   (size / 2) * 0.7,
    //   size / 2,
    //   size / 2,
    //   size / 2
    // );
    // gradient.addColorStop(0, "transparent");
    // gradient.addColorStop(0.8, "black");
    // gradient.addColorStop(1, "black");

    // // Apply the gradient to the canvas
    // context.fillStyle = gradient;
    // context.fillRect(0, 0, size, size);

    // // Create a texture from the canvas
    // const texture = new THREE.CanvasTexture(canvas);

    // // Create a ring geometry and apply the gradient texture
    // const ringGeometry = new THREE.RingGeometry(
    //   (gridSize / 2) * 0.7,
    //   gridSize / 2,
    //   64
    // );
    // const ringMaterial = new THREE.MeshBasicMaterial({
    //   map: texture,
    //   side: THREE.DoubleSide,
    //   transparent: true,
    // });
    // const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    // ring.rotation.x = Math.PI / 2; // Rotate the ring to lie flat on the grid
    // ring.position.y = 1; // Raise the ring slightly above the grid
    // grid.current = ring;
    // scene.current.add(ring);
  };
  function checkIntersections(string) {
    // console.log("modelNodes",modelNodes)
    // Update the picking ray with the camera and mouse position
    raycaster.setFromCamera(mouse, camera.current);
    // console.log("scene.current.children",scene.current.children)
    // Calculate objects intersecting the picking ray
    const intersects = raycaster.intersectObjects(scene.current.children, true);
    // If intersects is not empty, intersects[0] is the closest intersected object
    if (intersects && intersects.length > 0) {
      // console.log(intersects)

      // strings.forEach((string) => {
      //   if (intersects[0].object.name.includes(string)) {

      const node = intersects.find((node) => node.object.name.includes(string));
      // const floorNode = intersects.find((node) =>
      //   node.object.name.includes("bbox")
      // );
      return node;
    } else {
      return null;
    }
  }
  const moveToApartmentNode = async (apartmentNode) => {
    console.log("moveToApartmentNode position", apartmentNode.position);
    if (apartmentNode && apartmentNode.children.length) {
      const targetAreaNodeName = "balcony"
      const targetArea = apartmentNode.children.find((n) =>
        n.name.includes(targetAreaNodeName)
      );
      console.log(targetAreaNodeName, targetArea.position);
      const targetNode = targetArea.children.find((n) =>
        n.name.includes("target")
      );

      // console.log("targetNode", targetNode);
      if (targetNode) {
        // boundaryNode.current = targetArea;
        targetNode.visible = false;
        const livingRoomTargetPosition = targetNode.position;
        const worldPosition = targetNode.localToWorld(
          new THREE.Vector3(0, 0, 0)
        );
        console.log("worldPosition", worldPosition);
        animateLookAtPosition(worldPosition, 2);
        // setControlsTarget(worldPosition);
      } else {
        alert('no room target')
      }

      const boundingBox = new THREE.Box3().setFromObject(targetArea);
      const center = boundingBox.getCenter(new THREE.Vector3());

      // add human height
      center.y += 2;
      const straightForwardQuaterion = new THREE.Quaternion();
      // animateLookAtPosition(livingRoomTargetPosition, 2);

      await animateCamera(camera.current, center, straightForwardQuaterion, 2);
    }
  };
  const moveToNode = (node) => {
    console.log("moveToNode", node);
    if (node) {
      const boundingBox = new THREE.Box3().setFromObject(node);
      const center = boundingBox.getCenter(new THREE.Vector3());
      center.y += 1;
      let straightForwardQuaterion = new THREE.Quaternion();
      const targetNode = node.children.find((n) => n.name.includes("target"));
      console.log("targetNode", targetNode);
      if (targetNode) {
        targetNode.visible = true;
        const targetNodePosition = targetNode.position;
        // calculateQuaternian to face the target node
        // const targetNodeDirection = new THREE.Vector3().subVectors(
        //   targetNodePosition,
        //   center
        // );
        // targetNodeDirection.y = 0; // Ignore the y component for horizontal rotation
        // targetNodeDirection.normalize();
        // straightForwardQuaterion.setFromUnitVectors(
        //   new THREE.Vector3(0, 0, -1),
        //   targetNodeDirection
        // );

        // animateLookAtPosition(targetNodePosition, 2);
      }
      animateCamera(camera.current, center, straightForwardQuaterion, 2);
    }
  };
  const handleSelectedPosition = (positionName) => {
    // console.log("handleSelectedPosition", position);
    const node = selectedApartmentNode.children.find((n) =>
      n.name.includes(positionName)
    );
    const target = node.children.find((n) =>
      n.name.includes("target")
    );
    moveToNode(node);
    if (target) {
      target.visible = true;
      setControlsTarget(target.position);
    }
  };
  const moveToDefaultView = () => {
    const targetPosition = projectDefaultView.default_view.position;
    animateCameraPosition(camera.current, targetPosition, 2);
  };
  const setAllApartmentsNodes = (ids = []) => {

    if (appRef.current) ids.push(appRef.current.id);

    if (apartmentNodes.current) {
      if (ids.length) {
        apartmentNodes.current.forEach((node) => {
          // console.log(node)
          if (node && node.userData.apartment) {
            // console.log("found node with app", node.userData.apartment)
            if (!ids.includes(node.userData.apartment.id)) {
              if (
                node &&
                (node.userData.apartment.available === "פנוי" ||
                  node.userData.apartment.available === "Available")
              ) {
                node.material = vacantColor.current;
              } else {
                node.material = occupiedColor.current;
              }
            } else {
              if (
                node &&
                (node.userData.apartment.available === "פנוי" ||
                  node.userData.apartment.available === "Available")
              ) {
                // calculate height of node
                const boundingBox = new THREE.Box3().setFromObject(node);
                const size = boundingBox.getSize(new THREE.Vector3());
                const height = size.y;
                // console.log("height", height)

                node.material = vacantHoveredColor.current;
              } else {
                node.material = occupiedHoveredColor.current;
              }
            }
          }
        });
      } else {
        apartmentNodes.current.forEach((node) => {
          // console.log(node)
          if (node && node.userData.apartment) {
            // console.log(node.userData.apartment.available)
            if (
              (node && node.userData.apartment.available === "פנוי") ||
              node.userData.apartment.available === "Available"
            ) {
              node.material = vacantColor.current;
            } else {
              node.material = occupiedColor.current;
            }
          }
        });
      }
    }
  };
  const toggleAreasVisibility = (visible) => {
    if (scene.current) {
      areaNodesRef.current.forEach((node) => {
        node.visible = visible;
      });
    }
  };

  function onMouseMove(event) {
    if (!mouse) return;
    if (!mouseDown && currentMode.current === DEFAULT) {
      const deltaX = mousePosition.current
        ? event.clientX - mousePosition.current.x
        : 0;
      const deltaY = mousePosition.current
        ? event.clientY - mousePosition.current.y
        : 0;
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      mousePosition.current = {
        x: event.clientX,
        y: event.clientY,
      };

      const apartmetNode = checkIntersections("bbox");
      const areaNode = checkIntersections("area");
      // console.log({areaNode})
      if (apartmetNode && apartmetNode.object.userData.apartment) {
        console.log(apartmetNode.object.userData.apartment.id)
        setAllApartmentsNodes([apartmetNode.object.userData.apartment.id]);
        onApartmentHover(
          apartmetNode.object.userData.apartment,
          mousePosition.current
        );
        document.getElementById("map").style.cursor = "pointer";
      }
      else if (areaNode) {
        areaNode.object.visible = true;
        document.getElementById("map").style.cursor = "pointer";
      }
      else {
        // console.log(appRef.current)
        setAllApartmentsNodes();
        onApartmentHover();
        toggleAreasVisibility(false);
        document.getElementById("map").style.cursor = "default";
      }
      setMousePosition(mousePosition.current);
    }
    // else  {
    //   // console.log(mouse.x)
    //   if (cameraMode.current === "Steady") {
    //     console.log({ deltaX, deltaY });
    //     const lootAt = calculateDestinationPoint({
    //       position: camera.current.position,
    //       quaternion: camera.current.quaternion,
    //     }, 100)
    //     //animatelookAtPosition
    //     const offSet = 1
    //     if (deltaX < 0) {
    //       lootAt.x += offSet;
    //     } else {
    //       lootAt.x -= offSet;

    //     }
    //     // if (deltaY < 0) {
    //     //   lootAt.y += offSet;
    //     // } else {
    //     //   lootAt.y -= offSet;
    //     // }

    //     lookAtPosition.current = lootAt;

    //   }
    //   // camera.current.lookAt(pos);
    // }
  }

  const saveProject = (form) => {
    console.log(form);

    let projectSettings = {};
    Object.keys(form.Project).forEach((key) => {
      projectSettings[form.Project[key].property] = form.Project[key].value;
    });
    Object.keys(form.Floors).forEach((key) => {
      projectSettings[form.Floors[key].property] = form.Floors[key].value;
    });
    const newSettings = {
      ...projectDefaultView,
      ...projectSettings,
    };
    console.log(newSettings);
    saveProjectSettings(newSettings);
  };
  const saveViewData = (type = "app", data) => {
    console.log("saving viewData", type, data);
    const viewData = getCurrentViewData(camera.current);
    let buildingToUpdate;
    switch (type) {
      case "app":
        saveApartmentViewData({
          ...viewedApartment.default_view,
          ...viewData,
        });
        break;
      case "app_view":
        saveApartmentViewData(data);
        break;
      case "building":
        buildingToUpdate = buildings.find(
          (b) => b.name == selectedBuildingName
        );
        saveBuildingCalibration(
          {
            ...buildingToUpdate.default_view,
            camera: viewData,
            position: modelsRef.current[selectedBuildingName].position,
            rotation: modelsRef.current[selectedBuildingName].rotation,
            scale: modelsRef.current[selectedBuildingName].scale,
            circleRadiusOffset:
              modelsRef.current[selectedBuildingName].circle.rasiusOffset,
            floorsLinesRotationY:
              modelsRef.current[selectedBuildingName].floorsGroup &&
                modelsRef.current[selectedBuildingName].floorsGroup.rotation.y
                ? modelsRef.current[
                  selectedBuildingName
                ].floorsGroup.rotation.y.toFixed(2)
                : 0,
          },
          buildingToUpdate.id
        );
        break;
      case "building-floors":
        buildingToUpdate = buildings.find(
          (b) => b.name == selectedBuildingName
        );
        saveBuildingCalibration(
          {
            ...buildingToUpdate.default_view,
            floorsView: viewData,
          },
          buildingToUpdate.id
        );
        break;
      case "project":
        const newSettings = {
          ...projectDefaultView,
          default_view: viewData,
        };
        saveProjectSettings(newSettings);
        break;

      default:
        break;
    }
  };

  const handleModelRotation = (rotationY) => {
    modelsRef.current[selectedBuildingName].rotation.y = rotationY / 10;
  };
  const handleFloorsRotation = (rotationY) => {
    modelsRef.current[selectedBuildingName].floorsGroup.rotation.y =
      rotationY / 10;
  };
  const handleModelScale = (scale) => {
    console.log(selectedBuildingName);
    console.log(modelsRef.current);
    modelsRef.current[selectedBuildingName].scale.set(scale, scale, scale);
    modelsRef.current[selectedBuildingName].floorsGroup.scale.set(
      scale,
      scale,
      scale
    );
  };
  const handleArrowsDistance = (distance) => {
    // console.log(arrowsGroup.current);
    // arrowsRadiusOffset.current = distance;
    const arrows = arrowsGroup.current.children;
    arrows.forEach((arrow) => {
      if (arrow.position.x > 0)
        arrow.position.x = initialArrowsRadius + +distance;
      if (arrow.position.x < 0)
        arrow.position.x = -initialArrowsRadius - +distance;
      if (arrow.position.z > 0)
        arrow.position.z = initialArrowsRadius + +distance;
      if (arrow.position.z < 0)
        arrow.position.z = -initialArrowsRadius - +distance;
    });
  };
  const handleArrowsRotation = (rotation) => {
    // console.log(arrowsGroup.current);
    // arrowsRadiusOffset.current = distance;
    arrowsGroup.current.rotation.y = rotation / 10;

  };

  const handleFloorsLinesColor = (color) => {
    floorLines.current.forEach((line) => {
      line.material.color.set(color);
    });
  };

  const updateLine = (line, start, end) => {
    // console.log({start, end})
    // Update the points in the geometry
    line.geometry.setFromPoints([start, end]);

    // Need to tell Three.js to update the line
    line.geometry.verticesNeedUpdate = true;
  };
  const updateLabelPosition = (label, position) => {
    label.position.set(position.x, position.y, position.z);
  };

  const updateLinesStart = (value) => {
    floorLineStartXOffset.current = value;
    floorLines.current.forEach((line) => {
      let startPos = { ...line.userData.startPos };
      let endPos = { ...startPos };

      if (line.userData.startPos.x > 0) {
        startPos.x = startPos.x + value;
        endPos.x = startPos.x + floorLinesWidth.current;
      } else {
        startPos.x = startPos.x - value;
        endPos.x = startPos.x - +floorLinesWidth.current;
      }
      updateLine(line, startPos, endPos);
      updateLabelPosition(line.label, endPos);
    });
  };
  const updatelinesWidth = (value) => {
    // console.log(floorLines.current);
    // console.log(floorLabels.current)
    floorLinesWidth.current = +value;
    floorLines.current.forEach((line) => {
      let startPos = { ...line.userData.startPos };
      let endPos = { ...startPos };
      if (line.userData.startPos.x > 0) {
        startPos.x += floorLineStartXOffset.current;
        endPos.x = startPos.x + +value;
      } else {
        startPos.x -= floorLineStartXOffset.current;
        endPos.x = startPos.x - +value;
      }
      updateLine(line, startPos, endPos);
      updateLabelPosition(line.label, endPos);
    });
  };
  const handleInputChange = (e, tab) => {
    // console.log(e);
    // console.log(tab);
    // console.log(modelsRef.current[selectedBuildingName])
    const { label, value } = e;
    switch (label) {
      case "X":
        modelsRef.current[selectedBuildingName].position.x = +value;
        break;
      case "Z":
        modelsRef.current[selectedBuildingName].position.z = +value;
        break;
      case ARROWS_DISTANCE:
        handleArrowsDistance(+value);

        break;
      case ARROWS_ROTATION:
        handleArrowsRotation(+value);

        break;
      case PODIUM_SCALE:
        changePodiumSize(+value);
        // if (tab == "Project") handleCircleSize(+value, mainCircle.current);
        // else
        //   handleCircleSize(+value, modelsRef.current[selectedBuildingName].circle);
        break;
      case "Circle size":
        if (tab == "Project") handleCircleSize(+value, mainCircle.current);
        else
          handleCircleSize(
            +value,
            modelsRef.current[selectedBuildingName].circle
          );
        break;
      case "Resize model":
        handleModelScale(+value);
        break;
      case ROTATE_MODEL:
        handleModelRotation(+value);
        break;
      case ROTATE_FLOORS:
        modelsRef.current[selectedBuildingName].floorsGroup &&
          handleFloorsRotation(+value);
        break;
      case "Line width":
        updatelinesWidth(+value);
        break;
      case "Line start":
        updateLinesStart(+value);

        break;
      case "Line color":
        console.log(floorLines.current);

        handleFloorsLinesColor(value);
        break;
      case "Max distance":
        controls.current.maxDistance = +value;
        break;
      case TRANSPARENCY:
        console.log("TRANSPARENCY", value);
        vacantColor.current.opacity = value;
        occupiedColor.current.opacity = value;
        break;
      case ROTATE_MODEL + "X":
        // tilesRenderer.current.group.rotation.x = +value ;
        break;
      default:
        break;
    }
  };
  let style = {
    position: "absolute",
    top: 0,
    left: !isRtl ? "auto" : '3rem',
    right: !isRtl ? '3rem' : "auto",
    backgroundColor: "lightgrey",
    color: "black",
  };
  const handleEndInitialAnimation = useCallback(() => {
    console.log("handleEndInitialAnimation");
    setAnimationEnded((pre) => !pre);
    setControls();
    enableControls();
    onAnimationEnd();
    setEnvironmentPreset("Sunset")
    setEnvironmentPreset("Sunset");
    setTimeout(() => {
      setAnimationEnded((pre) => !pre);
    }, 20);
  });
  const handleBackButtonClick = useCallback(() => {
    currentMode.current = DEFAULT
    moveCameraToLastView();
    onBackButtonClick();
    setLastCameraViewData(null);
    // setControlsTarget(initialLookAt);
    enableControls();
  }, [moveCameraToLastView, onBackButtonClick]);


  return (
    <>
      <div ref={mountRef} />
      {initialized && (
        <>
          <CameraController
            camera={camera.current}
            projectDefaultView={projectDefaultView}
            onAnimationEnd={handleEndInitialAnimation}
            modelsLoaded={modelsLoaded}
            animationEnded={animationEnded}
          // position
          />
          <Presets
            animationEnded={animationEnded}
            showPresets={showPresets}
            isRtl={isRtl}
            setPreset={setEnvironmentPreset}
            environmentPreset={environmentPreset}
            showBackButton={lastCameraViewData ? true : false}
            onBackButtonClick={handleBackButtonClick}
            show360={touredApartment ? true : false}
          />
          <EnvironmentManager
            scene={scene.current}
            renderer={renderer.current}
            animationEnded={animationEnded}
            selectedFloor={selectedFloor}
            floors={floors}
            environmentPreset={environmentPreset}
            modelsRef={modelsRef.current}
            setSelectedFloor={setSelectedFloor}
            isRtl={isRtl}
          />

          {/* <ModelsManager
            scene={scene.current}
            parent={selectedApartmentNode}
            setModelsLoaded={setModelsLoaded}
          /> */}
          <LightsManager scene={scene.current} parent={selectedApartmentNode} />
          <SkyController
            setEnvironmentPreset={setEnvironmentPreset}
            environmentPreset={environmentPreset}
            selectedFloor={selectedFloor}
            showOcean={projectDefaultView.showOcean}
            // gridSize={gridSize}
            scene={scene.current}
            renderer={renderer.current}
            camera={camera.current}
            animationEnded={animationEnded}
            modelsLoaded={modelsLoaded}
            isRtl={isRtl}
          />
          <Grid
            scene={scene.current}
            gridSize={gridSize}
            show={showGrid && animationEnded}
          />
          {tileSetRef && <TileSet 
            ref={tileSetRef}
            url={tileSetUrl}
            scene={scene.current}
            renderer={renderer.current}
            camera={camera.current}
            // rotation={{ x: -Math.PI / 2, y: 0, z: 0 }}
            // onLoadTileSet={(tileset) => console.log('Tileset loaded from ThreeScene:', tileset)}
            onError={(error) => console.error('Tileset error from ThreeScene:', error)}
          />}
          {/* <TexturesManager
            selectedApartmentNode={selectedApartmentNode}
            scene={scene.current}
          /> */}
          {touredApartment && <MovementControls colors={colors} isRtl={isRtl} />}
          {touredApartment && <MobileMovementController />}

        </>
      )}

      {isAdmin && (
        <div className="settings" style={style}>
          {viewedApartment ? (
            <ApartmentSettings
              apartment={viewedApartment}
              saveViewData={saveViewData}
              camera={camera.current}
            />
          ) : (
            <Settings
              app={viewedApartment}
              buildings={buildings}
              projectDefaultView={projectDefaultView}
              // settingsForm={settingsForm}
              saveStatus={saveStatus}
              saveProjectSettings={saveProjectSettings}
              onChange={handleInputChange}
              saveProject={(form) => saveProject(form)}
              saveViewData={saveViewData}
              selectedBuildingName={selectedBuildingName}
              setSelectedBuildingName={setSelectedBuildingName}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Scene;
